import type { AquiferFeature, SubBasinFeature } from "../model/gisLayer.ts";
import { aquiferName, modelTypeName, outletPointName, zoneName } from "../model/entityNames.ts";
import type { Aquifer, EvalHidProject, SubBasin } from "../model/types.ts";
import type { Translate } from "../i18n/hooks.ts";
import { escapeHtml } from "./escapeHtml.ts";

/**
 * Sub-basin linked to a map feature, always resolved against the live
 * `project.subBasins` (by `gisCode`), never against the snapshot left
 * embedded in `layer` when it was created — that `layer` (gisLayer) is
 * built once (on importing the shapefile, or on opening the project) and
 * isn't regenerated when zone/model assignments change later, so its
 * `feature.properties.subBasin` becomes stale. Same pattern already used by
 * AssignmentMap.tsx.
 */
export function subBasinOf(project: EvalHidProject, feature: SubBasinFeature): SubBasin | null {
  const idGis = feature.properties.idGis;
  return idGis == null
    ? null
    : (project.subBasins.find((c) => c.gisCode === Number(idGis)) ?? null);
}

/** Same reason as `subBasinOf`: resolves against the live `project.aquifers`, not the snapshot embedded in the layer when it was created. */
export function aquiferOf(project: EvalHidProject, feature: AquiferFeature): Aquifer | null {
  const cod = feature.properties.aquifer?.aquiferCode;
  return cod == null ? null : (project.aquifers.find((a) => a.aquiferCode === cod) ?? null);
}

export function popupContent(
  t: Translate,
  project: EvalHidProject,
  feature: SubBasinFeature,
  subBasin: SubBasin | null,
  problems: string[]
): string {
  const codMasa = (feature.properties.attributes?.CodMasa as string | undefined) ?? "";
  if (!subBasin) {
    return `<strong>${escapeHtml(codMasa || t("map.noName"))}</strong><br/><em>${escapeHtml(t("legend.unlinked"))}</em>`;
  }
  const lines = [
    `<strong>${escapeHtml(subBasin.description)}</strong>`,
    escapeHtml(t("map.area", { km2: subBasin.area.toFixed(1) })),
    escapeHtml(
      t("map.zone", {
        zone: zoneName(t, project, subBasin.zoneCode),
        model: modelTypeName(t, project, subBasin.modelTypeCode, 0),
      })
    ),
  ];
  if (subBasin.snowZoneCode != null) {
    lines.push(
      escapeHtml(
        t("map.snowZone", {
          zone: zoneName(t, project, subBasin.snowZoneCode),
          model: modelTypeName(t, project, subBasin.snowModelTypeCode, 1),
        })
      )
    );
  }
  lines.push(
    escapeHtml(t("map.outletColon", { name: outletPointName(t, project, subBasin.outletCode) }))
  );
  if (subBasin.aquiferCode !== 0) {
    lines.push(
      escapeHtml(t("map.aquiferColon", { name: aquiferName(project, subBasin.aquiferCode) }))
    );
  }
  for (const motivo of problems) {
    lines.push(`<em>⚠ ${escapeHtml(motivo)}</em>`);
  }
  return lines.join("<br/>");
}
